import React, { useEffect, useMemo, useState } from 'react';
import { reportHandledError } from '../utils/sentry';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  Percent, Plus, Search, ArrowRight, RefreshCcw,
  CheckCircle2, Layers, TrendingUp, ChevronDown, ChevronUp, UploadCloud, RefreshCw,
  Printer, MessageCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import PriceListImportModal from '../components/PriceListImportModal';
import PriceListShareModal from '../components/PriceListShareModal';
import { getPriceListSummaryLabel, isBulkPriceList } from '../utils/priceLists';
import { applyActiveProductsFilter } from '../utils/productStatus';
import { fetchAllRows } from '../utils/supabasePagination';

/**
 * Gestión de Listas de Precios - Estándar Impulza Enterprise
 * Layout alineado a Clientes (header + KPIs + buscador + listado)
 * ADAPTADO A ESTILO PRODUCTOS
 */
const ListasPrecios = () => {
  const navigate = useNavigate();
  const { profile, organization } = useAuth();

  const [loading, setLoading] = useState(true);
  const [listas, setListas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  // Import modal (crear nueva lista desde archivo)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importProducts, setImportProducts] = useState([]);
  const [loadingImportProducts, setLoadingImportProducts] = useState(false);

  // Imprimir / WhatsApp sin entrar a editar
  const [shareList, setShareList] = useState(null);
  const openShare = (e, lista) => {
    e.stopPropagation();
    setShareList(lista);
  };
  const ShareButtons = ({ lista }) => (
    <div className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={(e) => openShare(e, lista)}
        title="Imprimir lista de precios"
        className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-[#0B2A36] hover:border-slate-300 transition-all"
      >
        <Printer size={14} />
      </button>
      <button
        type="button"
        onClick={(e) => openShare(e, lista)}
        title="Mandar por WhatsApp"
        className="p-1.5 rounded-lg border border-emerald-100 bg-emerald-50 text-[#1faa53] hover:bg-emerald-100 transition-all"
      >
        <MessageCircle size={14} />
      </button>
    </div>
  );
  const ajusteLabel = (l) => {
    if (l.pricing_mode === 'fixed') return getPriceListSummaryLabel(l);
    return `Ajuste configurado: ${Number(l.adjustment_percentage || 0).toFixed(2)}%`;
  };

  useEffect(() => {
    if (profile?.organization_id) fetchListas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  const fetchListas = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('price_lists')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setListas(data || []);
    } catch (e) {
      console.error(e);
      reportHandledError(e, { feature: 'ventas:listas-precios' });
      toast.error('Error al sincronizar listas de precios');
      setListas([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenImport = async () => {
    setLoadingImportProducts(true);
    try {
      const { data, error } = await fetchAllRows(
        () => applyActiveProductsFilter(
          supabase
            .from('products')
            .select('id, name, barcode, sell_price, product_variants(price)')
            .eq('organization_id', profile.organization_id)
        ),
        { label: 'listasPreciosImport' }
      );

      if (error) throw error;
      setImportProducts((data || []).slice().sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'es')));
    } catch (e) {
      console.error(e);
      toast.error('No se pudo cargar el catalogo de productos');
      setImportProducts([]);
    } finally {
      setLoadingImportProducts(false);
    }
    setIsImportModalOpen(true);
  };

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return listas;

    return listas.filter(l =>
      (l.name || '').toLowerCase().includes(q) ||
      String(l.adjustment_percentage ?? '').includes(q)
    );
  }, [listas, searchTerm]);

  const getSortableValue = (item, key) => {
    switch (key) {
      case 'ref':
        return String(item?.id || '').toLowerCase();
      case 'name':
        return String(item?.name || '').toLowerCase();
      case 'adjustment_percentage':
        return Number(item?.adjustment_percentage || 0);
      case 'is_active':
        return item?.is_active !== false ? 1 : 0;
      case 'created_at':
        return new Date(item?.created_at || 0).getTime();
      default:
        return '';
    }
  };

  const sortedFiltered = useMemo(() => {
    const items = [...filtered];
    const { key, direction } = sortConfig;
    const factor = direction === 'asc' ? 1 : -1;

    items.sort((a, b) => {
      const aValue = getSortableValue(a, key);
      const bValue = getSortableValue(b, key);

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * factor;
      }

      return String(aValue).localeCompare(String(bValue), 'es-AR', { numeric: true, sensitivity: 'base' }) * factor;
    });

    return items;
  }, [filtered, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => (
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'adjustment_percentage' || key === 'is_active' || key === 'created_at' ? 'desc' : 'asc' }
    ));
  };

  const FilterHint = ({ text }) => (
    <span
      className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-200 bg-white text-[9px] font-black text-slate-400 cursor-help"
      title={text}
    >
      ?
    </span>
  );

  const SortableTh = ({ label, sortKey, align = 'left', title }) => {
    const isActive = sortConfig.key === sortKey;
    const Icon = isActive && sortConfig.direction === 'asc' ? ChevronUp : ChevronDown;
    const alignClass = align === 'center' ? 'justify-center text-center' : align === 'right' ? 'justify-end text-right' : 'justify-start text-left';

    return (
      <th className={`px-3.5 py-3 ${align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left'}`}>
        <button
          type="button"
          onClick={() => handleSort(sortKey)}
          title={title || `Ordenar por ${label}`}
          className={`inline-flex w-full items-center gap-1.5 font-black uppercase tracking-widest transition-colors ${alignClass} ${isActive ? 'text-slate-700' : 'text-slate-400 hover:text-slate-600'}`}
        >
          <span>{label}</span>
          <Icon size={12} className={isActive ? 'text-[#0B2A36]' : 'text-slate-300'} />
        </button>
      </th>
    );
  };

  const stats = useMemo(() => {
    const total = listas.length;
    const activas = listas.filter(l => l.is_active !== false).length;
    const avgAdj =
      total === 0
        ? 0
        : listas.reduce((acc, l) => acc + Number(l.adjustment_percentage || 0), 0) / total;

    return { total, activas, avgAdj };
  }, [listas]);

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin mb-4 text-[#51A7FD]" size={40} />
        <p className="font-bold uppercase tracking-[0.3em] text-slate-400 text-[10px]">
          Sincronizando Listas...
        </p>
      </div>
    );
  }

  return (
    <div className="w-full px-3 py-3 sm:px-4 sm:py-4 lg:px-5 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      
      {/* HEADER NUEVO (Estilo Productos) */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Percent size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">Listas de Precios</h1>
            <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Políticas comerciales y precios de referencia
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleOpenImport}
            disabled={loadingImportProducts}
            className="bg-[#0B2A36] text-white px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
          >
            {loadingImportProducts
              ? <div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
              : <UploadCloud size={16} strokeWidth={3} />}
            Importar Lista
          </button>
          <button
            onClick={() => navigate('/sistema/listas-precios/nueva')}
            className="bg-[#3ff2aa] text-[#0B2A36] px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-95 transition-all"
          >
            <Plus size={16} strokeWidth={3} /> Nueva Lista
          </button>
        </div>
      </div>

      {/* HEADER ANTERIOR (COMENTADO)
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 px-1">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white border border-slate-200 rounded-lg text-slate-400 shadow-sm">
            <Percent size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800 uppercase tracking-tight leading-none">
              Listas de Precios
            </h1>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1 italic">
              Políticas comerciales, ajustes y precios de referencia
            </p>
          </div>
        </div>

        <button
          data-onboarding-id="price-list-new-btn"
          onClick={() => navigate('/sistema/listas-precios/nueva')}
          className="bg-[#3ff2aa] text-[#0B2A36] px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-md hover:scale-[1.02] active:scale-95 transition-all shadow-emerald-50"
        >
          <Plus size={16} strokeWidth={3} /> Nueva Lista
        </button>
      </div>
      */}

      {/* KPIS NUEVO (Estilo Productos) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 mb-4">
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Registradas</p>
                <Layers size={16} className="text-slate-300" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{stats.total}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total en sistema</p>
        </div>

        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Activas</p>
                <CheckCircle2 size={16} className="text-slate-300" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-emerald-600 tracking-tight">{stats.activas}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Disponibles</p>
        </div>

        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Ajuste Promedio</p>
                <TrendingUp size={16} className="text-slate-300" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{Number(stats.avgAdj || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Global</p>
        </div>
      </div>

      {/* KPIS ANTERIORES (COMENTADO)
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4 transition-all hover:border-[#51A7FD]">
          <div className="p-3 rounded-xl bg-blue-50 text-[#51A7FD] shadow-inner">
            <Layers size={24} />
          </div>
          <div>
            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">
              Listas Registradas
            </p>
            <h3 className="text-xl font-black text-slate-800 tracking-tighter">{stats.total}</h3>
          </div>
        </div>
        ...
      </div>
      */}

      {/* BUSCADOR NUEVO (Estilo Productos) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 mb-4">
        <div className="mb-2 ml-1 flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-slate-400">
          <Search size={12} /> Buscar
          <FilterHint text="Filtra por nombre de lista o porcentaje de ajuste." />
        </div>
        <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
            <input
            type="text"
            placeholder="BUSCAR POR NOMBRE O % DE AJUSTE..."
            className="w-full pl-11 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            />
        </div>
      </div>

      {/* BUSCADOR ANTERIOR (COMENTADO)
      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
        <input
          type="text"
          placeholder="BUSCAR POR NOMBRE O % DE AJUSTE..."
          className="w-full bg-white border border-slate-200 pl-12 pr-4 py-4 rounded-2xl text-[11px] font-black uppercase outline-none focus:ring-4 focus:ring-[#51A7FD]/5 focus:border-[#51A7FD] transition-all shadow-sm shadow-slate-50"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      */}

      {/* LISTADO NUEVO (Estilo Productos) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden min-h-[500px] mb-10">
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Percent size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">
              Listado ({sortedFiltered.length})
            </span>
          </div>
        </div>

        <div className="lg:hidden p-3 space-y-2.5 bg-slate-50/40">
          {sortedFiltered.map((l, idx) => {
            const active = l.is_active !== false;
            const adj = Number(l.adjustment_percentage || 0);
            const autoSync = l.auto_sync_with_base_price === true && l.pricing_mode === 'fixed';

            return (
              <div
                key={l.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/sistema/listas-precios/editar/${l.id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/sistema/listas-precios/editar/${l.id}`); }}
                className="w-full cursor-pointer rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-sm transition-all hover:border-[#51A7FD]/30"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[11px] font-black text-slate-700 uppercase tracking-tight truncate">{l.name || 'SIN NOMBRE'}</div>
                    <div className="mt-1 text-[9px] font-bold text-slate-400 uppercase tracking-widest">Ref #{idx + 1}</div>
                    <div className="mt-2"><ShareButtons lista={l} /></div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border ${
                        active
                          ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                          : 'bg-slate-100 text-slate-500 border-slate-200'
                      }`}
                    >
                      {active ? 'Activa' : 'Inactiva'}
                    </span>
                    {autoSync && (
                      <span
                        title="Sincroniza automaticamente con el precio base de los productos"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest border bg-sky-50 text-sky-700 border-sky-100"
                      >
                        <RefreshCw size={10} /> Auto-sync
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2 text-[10px]">
                  <div className="rounded-xl bg-slate-50 px-3 py-2">
                    <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Ajuste</div>
                    {l.pricing_mode === 'fixed' ? (
                      <div className="mt-1 font-black text-slate-700">{isBulkPriceList(l) ? 'Porcentaje' : 'Por producto'}</div>
                    ) : (
                      <div className={`mt-1 font-black ${adj >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {adj >= 0 ? '+' : ''}{adj.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                      </div>
                    )}
                  </div>
                  <div className="rounded-xl bg-slate-50 px-3 py-2 text-right">
                    <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Parámetro</div>
                    <div className="mt-1 font-bold text-slate-600">{ajusteLabel(l)}</div>
                  </div>
                </div>
              </div>
            );
          })}

          {sortedFiltered.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-10 text-center opacity-30 italic text-[10px] font-black uppercase tracking-widest">
              Sin resultados para el filtro aplicado
            </div>
          )}
        </div>

        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-[920px] w-full text-left">
            <thead>
              <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                <SortableTh label="Ref" sortKey="ref" title="Ordenar por referencia interna" />
                <SortableTh label="Lista / Parámetros" sortKey="name" title="Ordenar por nombre de lista" />
                <SortableTh label="Ajuste" sortKey="adjustment_percentage" align="right" title="Ordenar por porcentaje de ajuste" />
                <SortableTh label="Estado" sortKey="is_active" align="right" title="Ordenar por estado de la lista" />
                <th className="px-3.5 py-3 w-28 text-right">Compartir</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-50 font-sans">
              {sortedFiltered.map((l, idx) => {
                const active = l.is_active !== false;
                const adj = Number(l.adjustment_percentage || 0);
                const autoSync = l.auto_sync_with_base_price === true && l.pricing_mode === 'fixed';

                return (
                  <tr
                    key={l.id}
                    onClick={() => navigate(`/sistema/listas-precios/editar/${l.id}`)}
                    className="hover:bg-blue-50/20 transition-all cursor-pointer group"
                  >
                    <td className="px-3.5 py-3.5">
                      <span className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">
                        #{idx + 1}
                      </span>
                    </td>

                    <td className="px-3.5 py-3.5">
                      <div className="flex items-center gap-4">
                        <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 border border-slate-100 shadow-sm group-hover:bg-[#51A7FD] group-hover:text-white transition-all">
                          <Percent size={16} />
                        </div>

                        <div className="flex flex-col">
                          <span className="text-[11px] font-black text-slate-700 uppercase tracking-tight group-hover:text-[#51A7FD] transition-colors leading-none mb-1">
                            {l.name || 'SIN NOMBRE'}
                          </span>
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                            {ajusteLabel(l)}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td className="px-3.5 py-3.5 text-right">
                      {l.pricing_mode === 'fixed' ? (
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                          {isBulkPriceList(l) ? getPriceListSummaryLabel(l) : 'Por producto'}
                        </span>
                      ) : (
                        <span className={`text-[12px] font-black tracking-tighter ${adj >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {adj >= 0 ? '+' : ''}{adj.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                        </span>
                      )}
                    </td>

                    <td className="px-3.5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        {autoSync && (
                          <span
                            title="Sincroniza automaticamente con el precio base de los productos"
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest border bg-sky-50 text-sky-700 border-sky-100"
                          >
                            <RefreshCw size={10} /> Auto-sync
                          </span>
                        )}
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border ${
                            active
                              ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                              : 'bg-slate-100 text-slate-500 border-slate-200'
                          }`}
                        >
                          {active ? 'Activa' : 'Inactiva'}
                        </span>
                      </div>
                    </td>

                    <td className="px-3.5 py-3.5 text-right whitespace-nowrap">
                      <span className="mr-2 align-middle"><ShareButtons lista={l} /></span>
                      <ArrowRight size={18} className="inline align-middle text-slate-200 group-hover:text-[#51A7FD] group-hover:translate-x-1 transition-all" />
                    </td>
                  </tr>
                );
              })}

              {sortedFiltered.length === 0 && (
                <tr>
                  <td colSpan="5" className="py-20 text-center opacity-30 italic text-[10px] font-black uppercase tracking-widest">
                    Sin resultados para el filtro aplicado
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PriceListShareModal
        open={!!shareList}
        onClose={() => setShareList(null)}
        priceList={shareList}
        organization={organization}
        orgId={profile?.organization_id}
      />

      {/* Modal: importar nueva lista de precios desde archivo */}
      <PriceListImportModal
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        products={importProducts}
        currentPrices={{}}
        mode="create"
        orgId={profile?.organization_id}
        onListCreated={() => {
          setIsImportModalOpen(false);
          fetchListas();
        }}
        priceListName=""
      />

    </div>
  );
};

export default ListasPrecios;
